import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Subscription, Observable, forkJoin } from "rxjs";
import { tap } from "rxjs/operators";
import { QueryResult, OnBaseDocumentType, OnBaseQueryModel, KeyValuePair, DocumentRecord, parseFileNameFromHeaders }from "../../../models/onBaseIntegrationAndMapping.model";
import { DocumentManagementService } from "../../../services/document-management.service";
import { LoaderService } from "../../../services/loader.service";
import { PageData } from "../../../models/paging.model";
import { PaginationComponent } from "../../pagination/pagination.component";
import { SortArgs } from "../../../../core/models/sort.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";

@Component({
    selector: 'document-search-results',
    templateUrl: './document-search-results.component.html',
    styleUrls: ['./document-search-results.component.scss'],
    standalone: false
})
export class DocumentSearchResultsComponent implements OnInit, OnDestroy {

    confirmDownloadAll: Dialog = new Dialog("You have requested the download of all of the current page of search results.  This could take a significant amount of time to process.  Are you sure you want to download all of the current page of documents?", [new DialogButton("Cancel", false, () => { return; }), new DialogButton("Confirm", true, () => { this.downloadAll(); } )])
    currentPage: PageData = { pageNumber: 1, resultsPerPage: 25 };

    @Output() pageChanged: EventEmitter<PageData> = new EventEmitter<PageData>();
    @Output() sortChanged: EventEmitter<SortArgs> = new EventEmitter<SortArgs>();

    @Input() query: OnBaseQueryModel;
    @ViewChild('downloadLink') public downloadLink: ElementRef;
    @ViewChild(PaginationComponent, { static: true }) pager: PaginationComponent;

    get pageNumber(): number {
        return this.query.pageNumber;
    }

    set pageNumber(value: number) {
        this.query.pageNumber = value;
        if (this.pager != null) {
            this.pager.pageNumber = value;
        }       
    }
    _result: QueryResult;
    get result(): QueryResult {
        return this._result;
    }

    @Input() set result(data: QueryResult) {
        this._result = data;
        if (this.query != null && data != null && this.pager != null) {
            this.pager.atSearchLimit = data.records.length < this.query.resultsPerPage;
        }
    }


    subscription: Subscription = new Subscription();
    @Output() throwError: EventEmitter<any> = new EventEmitter<any>();
    typeList: OnBaseDocumentType[];

    constructor(private _docService: DocumentManagementService, private _loader: LoaderService, private _dialogService: DynamicDialogService) { }

    ngOnInit() {
        const type$ = this._docService.getAllDocumentTypes().pipe(tap((types: OnBaseDocumentType[]) => {
            this.typeList = types;
        })
        ).subscribe(
            () => { },
            (error) => {
                console.error(error);
                this.handleError(error);
            }
        );
        this.subscription.add(type$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    confirmAll(): void {
        this.confirmDownloadAll.toggleDialog(true);
        this._dialogService.updateDialog(this.confirmDownloadAll);
    }

    download(id: number): void {
        this._loader.toggleLoader(true);
        const file$ = this.downloadObservable(id).pipe()
            .subscribe(
                (data) => {
                    console.log(`Document ${id} downloaded`);
                },
                (error) => {
                    console.error(error);
                    this.handleError(error);
                }
        );
        this.subscription.add(file$);
    }

    downloadAll(): void {
        const obs$: Observable<any>[] = [];
        if (this.result != null && this.result.records != null) {
            this.result.records.forEach(r => {
                obs$.push(this.downloadObservable(r.documentId));
            });
        }
        this._loader.toggleLoader(true);
        const allSubs$ = forkJoin(obs$).pipe()
            .subscribe(
                (data) => {
                    console.log(`${data.length} documents downloaded`);
                    this._loader.toggleLoader(false);
                },
                (error) => { this.handleError(error); }
        );
        this.subscription.add(allSubs$);
    }

    downloadObservable(id: number): Observable<any> {
        return this._docService.download(id).pipe(
            tap((data) => {
                this._loader.toggleLoader(false);
                const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                const blob = new Blob([data.body], { type: data.body.type });
				const blobUrl = window.URL.createObjectURL(blob);
				const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
				anchor.href = blobUrl;
				anchor.download = fileName;
				anchor.click();
            })
        );
    }

    handleError(error: any): void {
        this._loader.toggleLoader(false);
        this.throwError.emit(error);
    }

    filteredKeywords(result: DocumentRecord): KeyValuePair[] {
        const hiddenKeys: string[] = ["Document Name", "Document Type"];
        const validPairs: KeyValuePair[] = [];
        for (let p = 0; p < result.displayColumns.length; p++) {
            const isParam = this.keywordQueried(result.displayColumns[p]);
            const notHidden = hiddenKeys.findIndex(k => k === result.displayColumns[p].key) === -1
            const notFileName = result.displayColumns[p].key.toString().toLocaleLowerCase().includes("file name") === false;
            const notBlank = result.displayColumns[p].value != null && result.displayColumns[p].value.toString().length > 0;
            if (notBlank && ((notHidden && notFileName) || isParam)) {
                validPairs.push(result.displayColumns[p]);
            }
        }

        return validPairs;
    }

    keywordQueried(pair: KeyValuePair): boolean {
        return this.query.searchParameters.findIndex(param => param.keywordTypeName === pair.key && param.keywordValue == pair.value) !== -1;
    }

    onPageChanged(pagination: PageData): void {
        this.pageChanged.emit(pagination);
    }

    onSorting(args: SortArgs): void {
        this.sortChanged.emit(args);
    }

    

    parseFileNameFromKeywords(result: DocumentRecord): string {
        let fileName: string = result.name;
        let keywordNames = result.displayColumns.filter(c => c.key.toString().toLocaleLowerCase().includes("legacy file name"));
        if (keywordNames != null && keywordNames.length > 0) {
            if (keywordNames[0].value != null && keywordNames[0].value.toString().length > 0) {
                fileName = keywordNames[0].value;
            }
        }
        return fileName;
    }

    translateDocumentType(result: DocumentRecord): string {
        let translation: string = "";
        if (result != null && result.displayColumns != null) {
            let type = result.displayColumns.find(c => c.key === "Document Type");
            if (type != null) {
                const equiv: OnBaseDocumentType = this.typeList.find(t => t.id == type.value);
                if (equiv != null) {
                    translation = equiv.name;
                }
            }
        }
        return translation;
    }
}
